import React, { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import UtilisateursPage from './UtilisateursPage';
import UserPermissions from '../components/UserPermissions';
import AuditLogs from '../components/AuditLogs';
import UserScopes from '../components/UserScopes';
import InventorySession from '../components/InventorySession';
import DataImportExport from '../components/DataImportExport';
import BreakGlassAccess from '../components/BreakGlassAccess';

const ProfilPage: React.FC = () => {
  const { user, logout, isLoading } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'profile' | 'rights' | 'auth' | 'utilisateurs' | 'audit' | 'scopes' | 'inventory' | 'data' | 'breakglass'>('profile');
  const [showEditForm, setShowEditForm] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [showUserForm, setShowUserForm] = useState(false);

  const isAdmin = user?.role === 'ADMIN';

  // Debug: afficher les données utilisateur
  console.log('ProfilPage - User data:', user);
  console.log('ProfilPage - Is loading:', isLoading);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Rediriger vers la page de login si l'utilisateur n'est pas connecté
  React.useEffect(() => {
    if (!isLoading && !user) {
      navigate('/login');
    }
  }, [isLoading, user, navigate]);

  const handleEditProfile = () => {
    setShowEditForm(true);
  };

  const handleChangePassword = () => {
    setShowPasswordForm(true);
  };

  const handleCreateUser = () => {
    setShowUserForm(true);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="px-4 py-6">
          <h1 className="text-2xl font-bold text-gray-900">Profil</h1>
          <p className="text-gray-600 mt-1">Gérez votre compte et vos paramètres</p>
          
          {/* Description de l'onglet actif */}
          <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-900">
              {activeTab === 'profile' && '👤 Informations personnelles et paramètres de votre compte'}
              {activeTab === 'rights' && '🔐 Liste complète de vos permissions selon votre rôle'}
              {activeTab === 'auth' && '🔑 Gestion de votre mot de passe et sécurité du compte'}
              {activeTab === 'utilisateurs' && '👥 Gestion des utilisateurs de la plateforme (réservé aux admins)'}
              {activeTab === 'audit' && '📊 Historique de toutes vos actions dans le système (traçabilité RGPD)'}
              {activeTab === 'scopes' && '🏢 Sites et bâtiments auxquels vous avez accès (périmètre d\'action)'}
              {activeTab === 'inventory' && '📦 Gestion des sessions d\'inventaire physique (comptage, validation)'}
              {activeTab === 'data' && '📁 Import/Export de données en masse (objets, lieux) au format CSV'}
              {activeTab === 'breakglass' && '🚨 Accès d\'urgence temporaire avec justification (admin uniquement)'}
            </p>
          </div>
        </div>
      </div>

      {/* Navigation Mobile */}
      <div className="bg-white border-b">
        <div className="flex overflow-x-auto">
          <button
            onClick={() => setActiveTab('profile')}
            className={`px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 ${
              activeTab === 'profile'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            👤 Profil
          </button>
          <button
            onClick={() => setActiveTab('rights')}
            className={`px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 ${
              activeTab === 'rights'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            🔐 Droits
          </button>
          <button
            onClick={() => setActiveTab('auth')}
            className={`px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 ${
              activeTab === 'auth'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            🔑 Authentification
          </button>
          {isAdmin && (
            <button
              onClick={() => setActiveTab('utilisateurs')}
              className={`px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 ${
                activeTab === 'utilisateurs'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              👥 Utilisateurs
            </button>
          )}

          <button
            onClick={() => setActiveTab('audit')}
            className={`px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 ${
              activeTab === 'audit'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            📊 Audit
          </button>

          <button
            onClick={() => setActiveTab('scopes')}
            className={`px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 ${
              activeTab === 'scopes'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            🏢 Scopes
          </button>

          <button
            onClick={() => setActiveTab('inventory')}
            className={`px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 ${
              activeTab === 'inventory'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            📦 Inventaire
          </button>

          <button
            onClick={() => setActiveTab('data')}
            className={`px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 ${
              activeTab === 'data'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            📁 Données
          </button>

          <button
            onClick={() => setActiveTab('breakglass')}
            className={`px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 ${
              activeTab === 'breakglass'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            🚨 Urgence
          </button>
        </div>
      </div>

      {/* Contenu */}
      <div className="p-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">Chargement du profil...</p>
            </div>
          </div>
        ) : !user ? (
          <div className="text-center py-8">
            <p className="text-red-600">Erreur: Impossible de charger les données du profil</p>
            <button
              onClick={handleLogout}
              className="mt-4 bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700"
            >
              Se déconnecter
            </button>
          </div>
        ) : (
          <>
            {activeTab === 'profile' && (
          <div className="space-y-6">
            {/* Informations personnelles */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Informations personnelles</h2>
                <button
                  onClick={handleEditProfile}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                  ✏️ Modifier
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Nom</label>
                  <p className="mt-1 text-sm text-gray-900">{user?.name || 'Non renseigné'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <p className="mt-1 text-sm text-gray-900">{user?.email}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Rôle</label>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    user?.role === 'ADMIN' ? 'bg-red-100 text-red-800' :
                    user?.role === 'GESTIONNAIRE' ? 'bg-blue-100 text-blue-800' :
                    user?.role === 'INVENTORISTE' ? 'bg-green-100 text-green-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {user?.role === 'ADMIN' ? '👑 Administrateur' :
                     user?.role === 'GESTIONNAIRE' ? '👨‍💼 Gestionnaire' :
                     user?.role === 'INVENTORISTE' ? '📋 Inventoriste' :
                     '👁️ Lecteur'}
                  </span>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Membre depuis</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {user?.createdAt ? new Date(user.createdAt).toLocaleDateString('fr-FR') : 'Non disponible'}
                  </p>
                </div>
              </div>
            </div>

            {/* Actions rapides */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Actions rapides</h2>
              <div className="space-y-3">
                <button
                  onClick={handleChangePassword}
                  className="w-full flex items-center justify-between p-3 text-left text-gray-700 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <span>🔑 Changer le mot de passe</span>
                  <span>→</span>
                </button>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center justify-between p-3 text-left text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
                >
                  <span>🚪 Se déconnecter</span>
                  <span>→</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'rights' && (
          <div className="space-y-6">
            <UserPermissions />
          </div>
        )}

        {activeTab === 'auth' && (
          <div className="space-y-6">
            {/* Méthodes d'authentification */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Méthodes d'authentification</h2>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl">📱</span>
                    <div>
                      <p className="font-medium text-gray-900">Application mobile</p>
                      <p className="text-sm text-gray-600">Authentification par email/mot de passe</p>
                    </div>
                  </div>
                  <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                    ✅ Actif
                  </span>
                </div>
                
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl">🔐</span>
                    <div>
                      <p className="font-medium text-gray-900">OAuth (Google, Microsoft)</p>
                      <p className="text-sm text-gray-600">Connexion avec compte externe</p>
                    </div>
                  </div>
                  <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                    ⏳ Bientôt
                  </span>
                </div>
              </div>
            </div>

            {/* Sécurité */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Sécurité</h2>
              <div className="space-y-3">
                <button
                  onClick={handleChangePassword}
                  className="w-full flex items-center justify-between p-3 text-left text-gray-700 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <span>🔑 Changer le mot de passe</span>
                  <span>→</span>
                </button>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm font-medium text-gray-700">Dernière connexion</span>
                  <span className="text-sm text-gray-600">
                    {user?.lastLogin ? new Date(user.lastLogin).toLocaleString('fr-FR') : 'Non disponible'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

            {activeTab === 'utilisateurs' && isAdmin && (
              <div className="space-y-6">
                <UtilisateursPage />
              </div>
            )}

            {activeTab === 'audit' && (
              <div className="space-y-6">
                <AuditLogs showFilters={true} maxHeight="600px" />
              </div>
            )}

            {activeTab === 'scopes' && (
              <div className="space-y-6">
                <UserScopes readOnly={true} />
              </div>
            )}

            {activeTab === 'inventory' && (
              <div className="space-y-6">
                <InventorySession />
              </div>
            )}

            {activeTab === 'data' && (
              <div className="space-y-6">
                <DataImportExport />
              </div>
            )}

            {activeTab === 'breakglass' && (
              <div className="space-y-6">
                <BreakGlassAccess />
              </div>
            )}
          </>
        )}
      </div>

      {/* Modals */}
      {showEditForm && (
        <EditProfileModal
          user={user}
          onClose={() => setShowEditForm(false)}
        />
      )}

      {showPasswordForm && (
        <ChangePasswordModal
          onClose={() => setShowPasswordForm(false)}
        />
      )}

      {showUserForm && isAdmin && (
        <CreateUserModal
          onClose={() => setShowUserForm(false)}
        />
      )}
    </div>
  );
};

// Composants modaux
const EditProfileModal: React.FC<{ user: any; onClose: () => void }> = ({ user, onClose }) => {
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // TODO: Implémenter la mise à jour du profil
    console.log('Mise à jour du profil:', formData);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Modifier le profil</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Nom</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 transition-colors"
              >
                Annuler
              </button>
              <button
                type="submit"
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
              >
                Sauvegarder
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const ChangePasswordModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // TODO: Implémenter le changement de mot de passe
    console.log('Changement de mot de passe:', formData);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Changer le mot de passe</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Mot de passe actuel</label>
              <input
                type="password"
                value={formData.currentPassword}
                onChange={(e) => setFormData({ ...formData, currentPassword: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Nouveau mot de passe</label>
              <input
                type="password"
                value={formData.newPassword}
                onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Confirmer le mot de passe</label>
              <input
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 transition-colors"
              >
                Annuler
              </button>
              <button
                type="submit"
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
              >
                Changer
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const CreateUserModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'LECTEUR',
    themes: ['PEDAGOGIE'],
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // TODO: Implémenter la création d'utilisateur
    console.log('Création d\'utilisateur:', formData);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Créer un utilisateur</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Nom</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Rôle</label>
              <select
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              >
                <option value="LECTEUR">👁️ Lecteur</option>
                <option value="INVENTORISTE">📋 Inventoriste</option>
                <option value="GESTIONNAIRE">👨‍💼 Gestionnaire</option>
                <option value="ADMIN">👑 Administrateur</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Thèmes autorisés</label>
              <div className="mt-2 space-y-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.themes.includes('PEDAGOGIE')}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setFormData({ ...formData, themes: [...formData.themes, 'PEDAGOGIE'] });
                      } else {
                        setFormData({ ...formData, themes: formData.themes.filter(t => t !== 'PEDAGOGIE') });
                      }
                    }}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">📚 PÉDAGOGIE</span>
                </label>
              </div>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 transition-colors"
              >
                Annuler
              </button>
              <button
                type="submit"
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
              >
                Créer
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ProfilPage;
